import type { Api_Playlist, Api_Video } from '@core/api'
import { PlayerLayout } from '@core/player/types'
import type { TriggerSourceEnum } from '@core/tracking/triggerSourceTracking'
import { DEFAULT_PER_PAGE } from '@core/widgets/common'

export interface EmbedFeedInitialState {
  /**
   * Encoded if of video which is eligible for autoplay.
   * @example {autoplayId: video.encoded_id}
   */
  autoplayId: string | null
  /**
   * Cause queued by a user gesture (e.g. arrow click) to be applied to the
   * next viewport-resolved autoplay. Cleared once consumed.
   */
  pendingTriggerSource: TriggerSourceEnum | null
  /**
   * Whether any video has auto-played in this render yet, used to tell the
   * first autoplay (`autoplay_first`) apart from later scroll-driven plays.
   */
  hasAutoplayedOnce: boolean
  /**
   * Encoded if of video which previously auto-played.
   * @example {pausedAutoplayId: video.encoded_id}
   */
  pausedAutoplayId: string | null
  /**
   * List of thumbnails which entered viewport and needs to be tracked
   *  @example [video.encoded_id,...]
   */
  inViewportIdsReportQueue: string[]
  /**
   * List of thumbnails already tracked to avoid duplicate tracking
   * @example [video.encoded_id,...]
   */

  inViewportIdsReportSuccess: string[]
  /**
   * List of videos which entered viewport and needs to be tracked
   * @example feedVideosInViewportQueue = [video,...]
   */
  feedVideosInViewportQueue: Api_Video[]
  /**
   * List of videos which entered viewport and have been successfully tracked
   * @example feedVideosInViewportSuccess = [video,...]
   */
  feedVideosInViewportSuccess: Api_Video[]
  /**
   * Map that holds thumbnails' in-the-viewport status
   * @example {[video.encoded_id]: true|false}
   */
  inViewportIds: Record<string, boolean>
  /**
   * State of the player.
   */
  player: {
    isOpen: boolean
    layout: PlayerLayout
    video?: Api_Video | null
    playlist?: Api_Playlist
  }
  /**
   * Thumbnail videos that have been loaded.
   * @example {[video.encoded_id]: true|false}
   */
  thumbnailIdsLoaded: Record<string, boolean>
  /**
   * Thumbnail videos that have been successfully processed.
   * @example {[video.encoded_id]: true|false}
   */
  thumbnailImpressionIdsReportSuccess: Record<string, boolean>
  /**
   * List of thumbnail videos that are registered in queue awaiting to get processed.
   */
  thumbnailImpressionVideoReportQueue: Api_Video[]
  /**
   *
   */
  placeholders: unknown[]
}

/**
 *
 */
export const createEmbedFeedState = (): EmbedFeedInitialState => ({
  autoplayId: null,
  pendingTriggerSource: null,
  hasAutoplayedOnce: false,
  pausedAutoplayId: null,
  player: {
    isOpen: false,
    layout: PlayerLayout.DEFAULT,
    video: null,
  },
  inViewportIdsReportQueue: [],
  inViewportIdsReportSuccess: [],
  feedVideosInViewportQueue: [],
  feedVideosInViewportSuccess: [],
  inViewportIds: {},
  thumbnailIdsLoaded: {},
  thumbnailImpressionIdsReportSuccess: {},
  thumbnailImpressionVideoReportQueue: [],
  placeholders: [...new Array(DEFAULT_PER_PAGE)],
})
